import React, { useState } from 'react';
import { Settings as SettingsIcon, Bell, Shield, Palette, Globe, User, Lock, Eye, EyeOff } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';

const Settings: React.FC = () => {
  const { settings, updateSettings } = useSettings();
  const [activeTab, setActiveTab] = useState('profile');

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'privacy', label: 'Privacy', icon: Shield },
    { id: 'appearance', label: 'Appearance', icon: Palette },
    { id: 'security', label: 'Security', icon: Lock }
  ];

  const accentColors = [
    { name: 'Cyan', value: 'cyan', preview: 'bg-cyan-500' },
    { name: 'Blue', value: 'blue', preview: 'bg-blue-500' },
    { name: 'Purple', value: 'purple', preview: 'bg-purple-500' },
    { name: 'Green', value: 'green', preview: 'bg-green-500' },
    { name: 'Pink', value: 'pink', preview: 'bg-pink-500' }
  ];

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Settings</h1>
          <p className="text-slate-400">Customize your SUI-ndicate experience</p>
        </div>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Settings Navigation */}
          <div className="lg:col-span-1">
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-4">Settings</h2>
              <nav className="space-y-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                      activeTab === tab.id
                        ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                        : 'text-slate-300 hover:text-cyan-400 hover:bg-slate-700/50'
                    }`}
                  >
                    <tab.icon className="w-5 h-5" />
                    <span>{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Settings Content */}
          <div className="lg:col-span-3">
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-8">
              {/* Profile Settings */}
              {activeTab === 'profile' && (
                <div>
                  <h2 className="text-2xl font-bold text-white mb-6">Profile Settings</h2>
                  <div className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-slate-400 text-sm mb-2">Display Name</label>
                        <input
                          type="text"
                          defaultValue="Crypto Investor"
                          className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-slate-400 text-sm mb-2">Email</label>
                        <input
                          type="email"
                          defaultValue="investor@sui-ndicate.com"
                          className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:outline-none"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-slate-400 text-sm mb-2">Bio</label>
                      <textarea
                        rows={4}
                        defaultValue="Passionate about blockchain technology and thoroughbred racing. Excited to be part of the future of decentralized horse racing syndicates."
                        className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:outline-none"
                      />
                    </div>
                    <div className="flex justify-end">
                      <button className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-3 rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all duration-200">
                        Save Changes
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Notification Settings */}
              {activeTab === 'notifications' && (
                <div>
                  <h2 className="text-2xl font-bold text-white mb-6">Notification Preferences</h2>
                  <div className="space-y-6">
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-white">Communication</h3>
                      {Object.entries(settings.notifications).map(([key, value]) => (
                        <div key={key} className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
                          <div>
                            <div className="text-white font-medium capitalize">{key.replace(/([A-Z])/g, ' $1')}</div>
                            <div className="text-slate-400 text-sm">
                              {key === 'email' && 'Receive email notifications'}
                              {key === 'push' && 'Receive push notifications'}
                              {key === 'sms' && 'Receive SMS alerts'}
                              {key === 'governance' && 'Governance proposals and voting'}
                              {key === 'racing' && 'Horse racing updates and results'}
                              {key === 'treasury' && 'Treasury updates and yields'}
                              {key === 'security' && 'Security alerts and login notifications'}
                            </div>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={value}
                              onChange={(e) => updateSettings({
                                notifications: { ...settings.notifications, [key]: e.target.checked }
                              })}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-cyan-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-500"></div>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Privacy Settings */}
              {activeTab === 'privacy' && (
                <div>
                  <h2 className="text-2xl font-bold text-white mb-6">Privacy & Security</h2>
                  <div className="space-y-6">
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-white">Profile Visibility</h3>
                      <div className="space-y-3">
                        {[
                          { value: 'public', label: 'Public', desc: 'Anyone can see your profile' },
                          { value: 'members', label: 'Members Only', desc: 'Only syndicate members can see your profile' },
                          { value: 'private', label: 'Private', desc: 'Only you can see your profile' }
                        ].map((option) => (
                          <label key={option.value} className="flex items-center space-x-3 p-4 bg-slate-700/50 rounded-lg cursor-pointer">
                            <input
                              type="radio"
                              name="profileVisibility"
                              value={option.value}
                              checked={settings.privacy.profileVisibility === option.value}
                              onChange={(e) => updateSettings({
                                privacy: { ...settings.privacy, profileVisibility: e.target.value as any }
                              })}
                              className="w-4 h-4 text-cyan-500 bg-slate-700 border-slate-600 focus:ring-cyan-500"
                            />
                            <div>
                              <div className="text-white font-medium">{option.label}</div>
                              <div className="text-slate-400 text-sm">{option.desc}</div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-white">Data Sharing</h3>
                      {[
                        { key: 'showBalance', label: 'Show Balance', desc: 'Display your balance in public profile' },
                        { key: 'showActivity', label: 'Show Activity', desc: 'Display your recent activity' },
                        { key: 'allowMessages', label: 'Allow Messages', desc: 'Allow other members to message you' }
                      ].map((item) => (
                        <div key={item.key} className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
                          <div>
                            <div className="text-white font-medium">{item.label}</div>
                            <div className="text-slate-400 text-sm">{item.desc}</div>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={privacy[item.key as keyof typeof privacy] as boolean}
                              onChange={(e) => setPrivacy({ ...privacy, [item.key]: e.target.checked })}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-cyan-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-500"></div>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Appearance Settings */}
              {activeTab === 'appearance' && (
                <div>
                  <h2 className="text-2xl font-bold text-white mb-6">Appearance & Theme</h2>
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-4">Theme Mode</h3>
                      <div className="grid grid-cols-2 gap-4">
                        {[
                          { value: 'dark', label: 'Dark Mode', desc: 'Futuristic dark theme' },
                          { value: 'light', label: 'Light Mode', desc: 'Clean light theme' }
                        ].map((mode) => (
                          <label key={mode.value} className="flex items-center space-x-3 p-4 bg-slate-700/50 rounded-lg cursor-pointer">
                            <input
                              type="radio"
                              name="themeMode"
                              value={mode.value}
                              checked={settings.theme === mode.value}
                              onChange={(e) => updateSettings({ theme: e.target.value as any })}
                              className="w-4 h-4 text-cyan-500 bg-slate-700 border-slate-600 focus:ring-cyan-500"
                            />
                            <div>
                              <div className="text-white font-medium">{mode.label}</div>
                              <div className="text-slate-400 text-sm">{mode.desc}</div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-white mb-4">Accent Color</h3>
                      <div className="grid grid-cols-5 gap-4">
                        {accentColors.map((color) => (
                          <label key={color.value} className="flex flex-col items-center space-y-2 cursor-pointer">
                            <input
                              type="radio"
                              name="accentColor"
                              value={color.value}
                              checked={settings.accent === color.value}
                              onChange={(e) => updateSettings({ accent: e.target.value as any })}
                              className="sr-only"
                            />
                            <div className={`w-12 h-12 rounded-full ${color.preview} ${
                              settings.accent === color.value ? 'ring-4 ring-cyan-500 ring-offset-2 ring-offset-slate-800' : ''
                            }`}></div>
                            <span className="text-slate-400 text-sm">{color.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-white">Effects</h3>
                      {[
                        { key: 'animations', label: 'Animations', desc: 'Enable smooth animations and transitions' },
                        { key: 'particles', label: 'Particle Effects', desc: 'Show floating particle effects' }
                      ].map((item) => (
                        <div key={item.key} className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
                          <div>
                            <div className="text-white font-medium">{item.label}</div>
                            <div className="text-slate-400 text-sm">{item.desc}</div>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={settings[item.key as keyof typeof settings] as boolean}
                              onChange={(e) => updateSettings({ [item.key]: e.target.checked })}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-cyan-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-500"></div>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Security Settings */}
              {activeTab === 'security' && (
                <div>
                  <h2 className="text-2xl font-bold text-white mb-6">Security Settings</h2>
                  <div className="space-y-6">
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-white">Two-Factor Authentication</h3>
                      <div className="p-6 bg-slate-700/50 rounded-lg">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <div className="text-white font-medium">2FA Status</div>
                            <div className="text-slate-400 text-sm">Add an extra layer of security</div>
                          </div>
                          <span className="px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-sm">Disabled</span>
                        </div>
                        <button className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-4 py-2 rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all duration-200">
                          Enable 2FA
                        </button>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-white">Session Management</h3>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
                          <div>
                            <div className="text-white font-medium">Active Sessions</div>
                            <div className="text-slate-400 text-sm">Current device - Chrome on Windows</div>
                          </div>
                          <button className="text-red-400 hover:text-red-300">End Session</button>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-white">Password</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-slate-400 text-sm mb-2">Current Password</label>
                          <input
                            type="password"
                            className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-slate-400 text-sm mb-2">New Password</label>
                          <input
                            type="password"
                            className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-slate-400 text-sm mb-2">Confirm New Password</label>
                          <input
                            type="password"
                            className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:outline-none"
                          />
                        </div>
                        <button className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-3 rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all duration-200">
                          Update Password
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
